import React, { useState, useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'

import { Theme, ThemeBorderRadius } from '~common/theme/types'
import { Icon, ICON_MAP } from '~common/components/icon'
import { LoadingIndicator } from '~common/components/loading'
import { TooltipComponent } from '~common/components/tooltip-component'

export interface ButtonProps {
    id?: string
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'glass'
    size?: 'small' | 'sm' | 'medium' | 'large' | 'very small'
    disabled?: boolean
    fullWidth?: boolean
    loading?: boolean
    borderRadius?: keyof ThemeBorderRadius
    status?: 'error' | 'warning' | 'info' | 'success'
    icon?: React.ReactNode | keyof typeof ICON_MAP
    iconPosition?: 'left' | 'right'
    height?: string
    label?: string | React.ReactNode
    tooltip?: string
    onClick?:
        | ((event: React.MouseEvent<HTMLButtonElement>) => void)
        | (() => void)
        | (() => Promise<void>)
    type?: 'button' | 'submit' | 'reset'
    maxWidth?: string
    className?: string
    onMouseEnter?: () => void
    onMouseLeave?: () => void
    tabIndex?: number
    dropdown?: React.ReactNode
}
const getIconColor = (
    variant: ButtonProps['variant'],
    status?: ButtonProps['status'],
) => {
    if (status === 'error') {
        return ({ theme }: { theme: Theme }) => theme.colors.error
    }
    if (status === 'warning') {
        return ({ theme }: { theme: Theme }) => theme.colors.warning
    }
    if (status === 'info') {
        return ({ theme }: { theme: Theme }) => theme.colors.info
    }
    switch (variant) {
        case 'primary':
            return ({ theme }: { theme: Theme }) => theme.colors.greyScale[1000]
        case 'secondary':
            return ({ theme }: { theme: Theme }) => theme.colors.greyScale[50]
        case 'outline':
            return ({ theme }: { theme: Theme }) => theme.colors.greyScale[600]
        case 'ghost':
            return ({ theme }: { theme: Theme }) => theme.colors.greyScale[100]
        case 'glass':
            return ({ theme }: { theme: Theme }) => theme.colors.greyScale[50]
        default:
            return ({ theme }: { theme: Theme }) => theme.colors.greyScale[1000]
    }
}
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            id,
            variant = 'primary',
            size = 'medium',
            disabled = false,
            fullWidth = false,
            maxWidth,
            type = 'button',
            loading = false,
            borderRadius,
            icon,
            iconPosition = 'left',
            status,
            label,
            tooltip,
            onClick,
            onMouseEnter,
            onMouseLeave,
            tabIndex,
            dropdown,
            ...props
        },
        ref,
    ) => {
        const isDisabled = disabled || loading
        const [isDropdownOpen, setIsDropdownOpen] = useState(false)
        const containerRef = useRef<HTMLDivElement>(null)

        useEffect(() => {
            if (!isDropdownOpen) return

            const handleClickOutside = (event: MouseEvent) => {
                if (
                    containerRef.current &&
                    !containerRef.current.contains(event.target as Node)
                ) {
                    setIsDropdownOpen(false)
                }
            }

            const handleEscape = (event: KeyboardEvent) => {
                if (event.key === 'Escape') {
                    setIsDropdownOpen(false)
                }
            }

            document.addEventListener('mousedown', handleClickOutside)
            document.addEventListener('keydown', handleEscape)

            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
                document.removeEventListener('keydown', handleEscape)
            }
        }, [isDropdownOpen])

        const getLoadingVariant = () => {
            switch (variant) {
                case 'primary':
                    return 'neutral'
                case 'secondary':
                    return 'neutral'
                case 'outline':
                case 'ghost':
                    return 'primary'
                default:
                    return 'neutral'
            }
        }

        const handleDropdownToggle = (
            event: React.MouseEvent<HTMLDivElement>,
        ) => {
            event.stopPropagation()
            setIsDropdownOpen(!isDropdownOpen)
        }

        const buttonContent = (
            <StyledButton
                id={id}
                ref={ref}
                {...props}
                variant={variant}
                size={size}
                disabled={isDisabled}
                fullWidth={fullWidth}
                type={type}
                loading={loading}
                borderRadius={borderRadius ?? 'full'}
                label={label}
                hasDropdown={!!dropdown}
                onClick={(event) => {
                    event.stopPropagation()
                    onClick?.(event)
                }}
                status={status}
                maxWidth={maxWidth}
                onMouseEnter={() => onMouseEnter?.()}
                onMouseLeave={() => onMouseLeave?.()}
                tabIndex={tabIndex}
            >
                {loading && iconPosition === 'left' && (
                    <LoadingIndicator
                        size="small"
                        variant={getLoadingVariant()}
                    />
                )}
                {icon &&
                    iconPosition === 'left' &&
                    (typeof icon === 'string' ? (
                        <Icon
                            name={icon}
                            size={size}
                            iconColor={getIconColor(variant, status)}
                        />
                    ) : (
                        icon
                    ))}
                {label && <LabelText>{label}</LabelText>}
                {icon &&
                    iconPosition === 'right' &&
                    !dropdown &&
                    (typeof icon === 'string' ? (
                        <Icon
                            name={icon}
                            size={size}
                            iconColor={getIconColor(variant, status)}
                        />
                    ) : (
                        icon
                    ))}
                {loading && iconPosition === 'right' && !dropdown && (
                    <LoadingIndicator
                        size="small"
                        variant={getLoadingVariant()}
                    />
                )}
                {dropdown && (
                    <>
                        <Icon
                            name="caret-down"
                            size={size}
                            iconColor={getIconColor(variant, status)}
                            onClick={handleDropdownToggle}
                        />
                    </>
                )}
            </StyledButton>
        )

        if (dropdown) {
            return (
                <ButtonContainer
                    ref={containerRef}
                    size={size}
                    label={label}
                    fullWidth={fullWidth}
                >
                    {buttonContent}
                    {dropdown}
                </ButtonContainer>
            )
        }

        if (tooltip) {
            return (
                <TooltipComponent
                    showArrow={false}
                    size="small"
                    content={tooltip}
                    position="bottom"
                >
                    {buttonContent}
                </TooltipComponent>
            )
        }

        return buttonContent
    },
)

const LabelText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
`

const getVariantStyles = (variant: ButtonProps['variant'] = 'primary') => {
    switch (variant) {
        case 'primary':
            return css`
                background-color: ${({ theme }) => theme.colors.greyScale[50]};
                color: ${({ theme }) => theme.colors.greyScale[1000]};
                border: 1px solid ${({ theme }) => theme.colors.greyScale[200]};
            `
        case 'secondary':
            return css`
                background-color: ${({ theme }) => theme.colors.greyScale[400]};
                color: ${({ theme }) => theme.colors.greyScale[200]};
                border: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
                color: ${({ theme }) => theme.colors.greyScale[50]};

                &:hover:not(:disabled) {
                    background-color: ${({ theme }) =>
                        theme.colors.greyScale[300]};
                    border-color: ${({ theme }) => theme.colors.greyScale[200]};
                }

                &:active:not(:disabled) {
                    background-color: ${({ theme }) =>
                        theme.colors.greyScale[700]};
                    border-color: ${({ theme }) => theme.colors.greyScale[700]};
                }
            `
        case 'outline':
            return css`
                background-color: transparent;
                color: ${({ theme }) => theme.colors.greyScale[100]};
                border: 1px solid ${({ theme }) => theme.colors.greyScale[500]};

                &:hover:not(:disabled) {
                    background-color: ${({ theme }) =>
                        theme.colors.greyScale[500]};
                    color: ${({ theme }) => theme.colors.text.inverse};
                }

                &:active:not(:disabled) {
                    background-color: ${({ theme }) =>
                        theme.colors.greyScale[600]};
                    border-color: ${({ theme }) => theme.colors.greyScale[600]};
                }
            `
        case 'ghost':
            return css`
                background-color: transparent;
                color: ${({ theme }) => theme.colors.greyScale[100]};
                border: 1px solid transparent;

                &:hover:not(:disabled) {
                    background-color: ${({ theme }) =>
                        theme.colors.greyScale[400]};
                }

                &:active:not(:disabled) {
                    background-color: ${({ theme }) =>
                        theme.colors.greyScale[400]};
                }
            `
        case 'glass':
            return css`
                background-color: transparent;
                color: ${({ theme }) => theme.colors.greyScale[100]};
                backdrop-filter: blur(2px);
                background-color: ${({ theme }) =>
                    theme.colors.background.bg2}01;

                &:hover:not(:disabled) {
                    background-color: ${({ theme }) =>
                        theme.colors.greyScale[400]};
                }

                &:active:not(:disabled) {
                    background-color: ${({ theme }) =>
                        theme.colors.greyScale[400]};
                }
            `
        default:
            return css``
    }
}

const getSizeStyles = (
    size: ButtonProps['size'] = 'medium',
    label: ButtonProps['label'],
) => {
    switch (size) {
        case 'small':
            return css`
                // prettier-ignore
                padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) =>
                    theme.spacing[2]};
                font-size: ${({ theme }) => theme.typography.fontSize.xs};
                border-radius: ${({ theme }) => theme.borderRadius.md};
                gap: ${({ theme }) => theme.spacing[1]};

                ${!label &&
                css`
                    padding: unset;
                    height: 30px;
                    width: 30px;
                `}
            `
        case 'medium':
            return css`
                // prettier-ignore
                padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) =>
                    theme.spacing[4]};
                font-size: ${({ theme }) => theme.typography.fontSize.base};
                border-radius: ${({ theme }) => theme.borderRadius.md};

                gap: ${({ theme }) => theme.spacing[2]};
                ${!label &&
                css`
                    padding: unset;
                    height: 34px;
                    width: 34px;
                `}
            `
        case 'large':
            return css`
                // prettier-ignore
                padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) =>
                    theme.spacing[6]};
                font-size: ${({ theme }) => theme.typography.fontSize.lg};
                border-radius: ${({ theme }) => theme.borderRadius.lg};
                gap: ${({ theme }) => theme.spacing[4]};

                ${!label &&
                css`
                    padding: unset;
                    height: 38px;
                    width: 38px;
                `}
            `
        case 'sm':
            return css`
                // prettier-ignore
                padding: ${({ theme }) => theme.spacing[1.5]} ${({ theme }) =>
                    theme.spacing[1.5]};
                font-size: ${({ theme }) => theme.typography.fontSize.xs};
            `
        default:
            return css``
    }
}

const ButtonContainer = styled.div<{
    size: ButtonProps['size']
    label: ButtonProps['label']
    fullWidth: ButtonProps['fullWidth']
}>`
    display: inline-flex;
    position: relative;
    box-sizing: border-box;

    ${({ size, label }) => getSizeStyles(size, label)}
    padding: 0;
    ${({ fullWidth }) =>
        fullWidth &&
        css`
            width: 100%;
        `}
`

const StyledButton = styled.button.withConfig({
    shouldForwardProp: (prop) =>
        ![
            'variant',
            'size',
            'fullWidth',
            'loading',
            'height',
            'status',
            'hasDropdown',
            'borderRadius',
        ].includes(prop),
})<ButtonProps & { hasDropdown?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    cursor: pointer;
    transition: all ${({ theme }) => theme.transitions.normal};
    border: none;
    outline: none;
    text-decoration: none;
    ${({ variant }) => getVariantStyles(variant)}
    ${({ size, label }) => getSizeStyles(size, label)}
    box-sizing: border-box;
    min-width: fit-content;

    ${({ borderRadius, theme, hasDropdown }) => {
        if (!borderRadius) return ''
        const radius = theme.borderRadius[borderRadius]
        if (hasDropdown) {
            return css`
                border-top-left-radius: ${radius};
                border-bottom-left-radius: ${radius};
                border-top-right-radius: ${radius};
                border-bottom-right-radius: ${radius};
            `
        }
        return `border-radius: ${radius};`
    }}

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.colors.greyScale[500]};
        outline-offset: 1px;
    }

    ${({ loading }) =>
        loading &&
        css`
            cursor: not-allowed;
            pointer-events: none;
        `}
    ${({ maxWidth }) =>
        maxWidth &&
        css`
            max-width: ${maxWidth};
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        `}
        white-space: nowrap;

    ${({ hasDropdown }) =>
        hasDropdown &&
        css`
            overflow: visible;
        `}
    ${({ fullWidth }) =>
        fullWidth &&
        css`
            width: 100%;
        `}
    ${({ height }) =>
        height &&
        css`
            height: ${height};
        `}

    ${({ status }) =>
        status === 'warning' &&
        css`
            border-color: ${({ theme }) => theme.colors.warning};
            &:hover:not(:disabled) {
                color: ${({ theme }) => theme.colors.warning};
                border-color: ${({ theme }) => theme.colors.warning};
            }

            &:active:not(:disabled) {
                color: ${({ theme }) => theme.colors.warning};
                border-color: ${({ theme }) => theme.colors.warning};
            }
        `}
`
